import { useState, useEffect } from 'react';
import { DashboardService } from '@/services/dashboardService';
import { supabase } from '@/lib/supabase';

export function useJobDescriptions() {
  const [jobDescriptions, setJobDescriptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJobDescriptions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) {
        // Handle RLS policy errors gracefully
        if (profileError.code === '42501' || profileError.message?.includes('policy') || profileError.message?.includes('406')) {
          console.log('Profile access blocked by RLS - user likely in onboarding');
          setJobDescriptions([]);
          return;
        } else if (profileError.code !== 'PGRST116') {
          throw profileError;
        }
      }

      if (!profile?.organization_id) {
        // User doesn't have organization yet, return empty array instead of error
        setJobDescriptions([]);
        return;
      }

      // Use the new service method for generated job descriptions
      const descriptions = await DashboardService.getJobDescriptionsGenerated(profile.organization_id);
      setJobDescriptions(descriptions);
    } catch (err) {
      console.error('Error fetching job descriptions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch job descriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = () => {
    fetchJobDescriptions();
  };

  useEffect(() => {
    fetchJobDescriptions();
  }, []);

  // Listen for job description updates
  useEffect(() => {
    const handleJobDescriptionUpdate = () => {
      fetchJobDescriptions();
    };

    window.addEventListener('jobDescriptionSaved', handleJobDescriptionUpdate);
    window.addEventListener('jobDescriptionUpdated', handleJobDescriptionUpdate);
    
    return () => {
      window.removeEventListener('jobDescriptionSaved', handleJobDescriptionUpdate);
      window.removeEventListener('jobDescriptionUpdated', handleJobDescriptionUpdate);
    };
  }, []);

  return {
    jobDescriptions,
    isLoading,
    error,
    refetch
  };
} 